import React, { useLayoutEffect, useRef, useState } from 'react'
import { Box } from '@material-ui/core'
import { tokens } from '../../styles/tokens'
import { useOsScale } from '../../common/helpers/useOsScale'

export interface IYumEmptyStateProps {
  text: string
  Icon: any
  extraPx?: number
  fullHeight?: boolean
}

export default function YumEmptyState(props: IYumEmptyStateProps) {
  const { text, Icon, extraPx, fullHeight } = props
  const ref = useRef<HTMLDivElement>(null)
  // Re-measure whenever the OS text scale or the viewport size changes — both
  // affect the height of the chrome above us (headers, tabs, possibly an extra
  // row for selects that wrap on enlarged text) and the chrome below us (fixed
  // bottom tab bar).
  const osScale = useOsScale()
  const [measuredHeight, setMeasuredHeight] = useState<number | null>(null)

  useLayoutEffect(() => {
    if (fullHeight) return
    if (!ref.current) return
    const measure = () => {
      if (!ref.current) return
      const container =
        (ref.current.closest('.layout__content') as HTMLElement | null) ||
        (document.querySelector('.layout__content') as HTMLElement | null)
      if (!container) {
        setMeasuredHeight(null)
        return
      }
      const boxRect = ref.current.getBoundingClientRect()
      const containerRect = container.getBoundingClientRect()
      const paddingBottom = parseFloat(getComputedStyle(container).paddingBottom) || 0
      let bottom: number
      if (getComputedStyle(container).position === 'fixed') {
        // Mobile: `.layout__content` is pinned between the top header and the
        // bottom tab bar. Its inner-bottom edge is the real available bottom.
        bottom = containerRect.bottom - paddingBottom
      } else {
        // Desktop: `.layout__content` is position:relative and grows with its
        // content, so its bottom is circular w.r.t. the box's own height.
        // Instead, take the viewport bottom and subtract the reserved space
        // between the box and the layout container — that captures things
        // like the surrounding Card's padding-bottom / margin-bottom so the
        // empty state stops at the visible content edge.
        const reservedBelow = Math.max(0, containerRect.bottom - paddingBottom - boxRect.bottom)
        bottom = window.innerHeight - reservedBelow
      }
      setMeasuredHeight(Math.max(0, bottom - boxRect.top - (extraPx || 0)))
    }
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [osScale, fullHeight, extraPx])

  // Desktop / first-paint fallback: static heuristic scaled with OS text size.
  const fallbackHeight = `calc(100vh - ${Math.round((278 + (extraPx || 0)) * osScale)}px)`
  const computedHeight = fullHeight ? '100%' : measuredHeight != null ? `${measuredHeight}px` : fallbackHeight

  return (
    <Box
      ref={ref}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        /* @ts-ignore */
        gap: '20px',
        height: computedHeight,
        // Icon inherits via currentColor (placeholder border tone).
        color: tokens.border.default,
      }}
    >
      {/* Decorative illustration — the accompanying text conveys the same meaning,
          so hide the SVG from assistive tech to avoid redundant announcements. */}
      <Icon style={{ width: '72px', height: '72px' }} aria-hidden="true" focusable="false" />
      {/* Text uses tokens.text.secondary (7.01:1) to meet WCAG 2.2 SC 1.4.3. */}
      <div style={{ color: tokens.text.secondary }}>{text}</div>
    </Box>
  )
}
